const INTERNAL_LABELS = {
  total_spans: 'total interactions',
  platform_metrics: 'platform metrics',
  page_summary: 'the current page',
  prompt_samples: 'example interactions',
  example_interactions: 'example interactions',
  dashboard_metrics: 'dashboard metrics',
  avg_session_quality: 'average session quality',
  avg_quality: 'average quality',
  topic_not_covered: 'topic not covered',
  permission_denied: 'permission denied',
  policy_restriction: 'policy restriction',
  data_source: 'data source',
  reporting_window: 'reporting window',
};

const PLAIN_ENGLISH_ACRONYMS = new Set(['ai', 'api', 'id', 'llm', 'pii', 'url']);
const INLINE_CODE_FILE_EXTENSIONS = new Set([
  'c', 'cpp', 'cs', 'css', 'csv', 'go', 'h', 'hpp', 'html', 'java', 'js', 'json',
  'jsx', 'md', 'php', 'ps1', 'py', 'rb', 'rs', 'sh', 'sql', 'toml', 'ts', 'tsx',
  'txt', 'xml', 'yaml', 'yml',
]);
const PROTECTED_TEXT_RE = /<[^>]+>|https?:\/\/[^\s<>]+|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const INDEX_NAME_RE = /\bsaf3ai[-_](?:[a-z0-9_.-]*[a-z0-9])\b/gi;
const FIELD_PATH_RE = /\b(?:[A-Za-z][A-Za-z0-9_]*\.)+[A-Za-z][A-Za-z0-9_]*\b/g;
const SNAKE_CASE_RE = /\b([A-Za-z][A-Za-z0-9]*(?:_[A-Za-z0-9]+)+)\b/g;
// At least two lowercase letters before the first capital, and a lowercase
// letter after each: dataDeleted / threatCount match, macOS / iPhone / iOS don't.
const CAMEL_CASE_RE = /\b([a-z]{2,}[a-z0-9]*(?:[A-Z][a-z0-9]+)+)\b/g;
const CAMEL_CASE_TOKEN_RE = /^[a-z]{2,}[a-z0-9]*(?:[A-Z][a-z0-9]+)+$/;
// Session / interaction / engine ids (sess_b3afb8fc01957573) are values the
// user may need to look up, not field names.
const OPAQUE_ID_RE = /\d{4,}/;

// Private Use Area sentinels are valid Unicode but do not occur in normal LLM
// prose, URLs, email addresses, or HTML. They let us temporarily remove those
// fragments without creating text that another humanizer regex can match.
const PROTECTED_FRAGMENT_START = '\uE000';
const PROTECTED_FRAGMENT_END = '\uE001';

function looksLikeInternalIdentifier(value) {
  if (OPAQUE_ID_RE.test(value)) return false;
  return Object.prototype.hasOwnProperty.call(INTERNAL_LABELS, value.toLowerCase())
    || value.includes('_')
    || CAMEL_CASE_TOKEN_RE.test(value)
    || (value.includes('.') && value.split('.').some(looksLikeInternalIdentifier));
}

function humanizeIdentifier(value) {
  const mapped = INTERNAL_LABELS[value.toLowerCase()];
  if (mapped) return mapped;

  const words = value
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/\s+/)
    .filter(Boolean);
  const normalizedMapping = INTERNAL_LABELS[words.join('_').toLowerCase()];
  if (normalizedMapping) return normalizedMapping;

  return words.map((word) => (PLAIN_ENGLISH_ACRONYMS.has(word.toLowerCase())
      ? word.toUpperCase()
      : word.toLowerCase()))
    .join(' ');
}

function humanizeFieldPath(value) {
  if (!looksLikeInternalIdentifier(value)) return value;
  return value.split('.').map(humanizeIdentifier).join(' ');
}

function shouldPreserveInlineCode(value) {
  const trimmed = String(value).trim();
  if (/^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/.test(trimmed)) return true;
  const extensionMatch = trimmed.match(/\.([A-Za-z0-9]+)$/);
  return Boolean(
    extensionMatch
    && !/\s/.test(trimmed)
    && INLINE_CODE_FILE_EXTENSIONS.has(extensionMatch[1].toLowerCase()),
  );
}

/**
 * Enforce the customer-facing language contract for Saf3AI Agent answers.
 *
 * Model output may echo field names from its data context even when prompted
 * not to. This display-boundary guard rewrites snake_case, lower camelCase,
 * dotted field paths, and internal index names while preserving URLs, email
 * addresses, and generated HTML tags.
 */
export function humanizeAgentResponse(value) {
  if (value === null || value === undefined) return '';
  let text = String(value);
  if (!text) return '';

  const protectedFragments = [];
  const protectFragment = (fragment) => {
    protectedFragments.push(fragment);
    return `${PROTECTED_FRAGMENT_START}${protectedFragments.length - 1}${PROTECTED_FRAGMENT_END}`;
  };
  text = text.replace(PROTECTED_TEXT_RE, protectFragment);

  text = text.replace(INDEX_NAME_RE, 'the platform data store');
  text = text.replace(/`([^`\n]+)`/g, (match, inner) => {
    if (
      !/\s/.test(inner)
      && !shouldPreserveInlineCode(inner)
      && looksLikeInternalIdentifier(inner)
    ) {
      return humanizeFieldPath(inner);
    }
    return protectFragment(match);
  });
  text = text.replace(FIELD_PATH_RE, humanizeFieldPath);
  text = text.replace(SNAKE_CASE_RE, (match) => (
    OPAQUE_ID_RE.test(match) ? match : humanizeIdentifier(match)
  ));
  text = text.replace(CAMEL_CASE_RE, (match) => humanizeIdentifier(match));

  protectedFragments.forEach((fragment, index) => {
    text = text.replace(
      `${PROTECTED_FRAGMENT_START}${index}${PROTECTED_FRAGMENT_END}`,
      fragment,
    );
  });
  return text;
}

/**
 * Humanize only schema labels in structured display metadata.
 *
 * Row/cell values are source data and must remain verbatim: an agent name,
 * filename, or quoted prompt can legitimately contain identifier-like text.
 */
export function humanizeAgentDisplayData(value) {
  if (Array.isArray(value)) {
    return value.map((item) => (
      item && typeof item === 'object' && !Array.isArray(item)
        ? humanizeAgentDisplayData(item)
        : item
    ));
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => {
        if (key === 'title' && typeof child === 'string') {
          return [key, humanizeAgentResponse(child)];
        }
        if (key === 'headers' && Array.isArray(child)) {
          return [key, child.map((header) => humanizeAgentResponse(String(header)))];
        }
        if (key === 'rows') return [key, child];
        return [key, humanizeAgentDisplayData(child)];
      }),
    );
  }
  return value;
}
